import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PriceDisclaimer } from "@/components/PriceDisclaimer";
import {
  User as UserIcon,
  Mail,
  Phone,
  MapPin,
  Edit3,
  Sprout,
  Leaf,
  Landmark,
  Crown,
  Smartphone,
  CreditCard,
  Building2,
  LogOut,
  ArrowRight,
  BadgeCheck,
  Sparkles,
  ShieldCheck,
  TrendingUp,
  TrendingDown,
  Award,
  Package,
  Activity,
  Minus,
} from "lucide-react";

const PostLoginDashboard = () => {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const d = t.dashboard;

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    api
      .get("/prices/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const grid = [
    {
      testid: "dash-khaad",
      icon: Sprout,
      title: d.khaad_title,
      sub: d.khaad_sub,
      desc: d.khaad_desc,
      color: "bg-[#1E4D2B]",
      to: "/khaad",
    },
    {
      testid: "dash-beej",
      icon: Leaf,
      title: d.beej_title,
      sub: d.beej_sub,
      desc: d.beej_desc,
      color: "bg-[#F59E0B]",
      to: "/beej",
    },
    {
      testid: "dash-schemes",
      icon: Landmark,
      title: d.schemes_title,
      sub: d.schemes_sub,
      desc: d.schemes_desc,
      color: "bg-[#14361E]",
      to: "/schemes",
    },
  ];

  return (
    <div data-testid="post-login-dashboard" className="bg-[#FDFBF7]">
      {/* Welcome header */}
      <section className="border-b border-stone-200 bg-gradient-to-b from-[#F4F1EA] to-[#FDFBF7]">
        <div className="container-gc px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex items-center gap-3 sm:gap-4">
            {user?.profile_picture ? (
              <img
                src={user.profile_picture}
                alt=""
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover border-2 border-white shadow-sm"
              />
            ) : (
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#1E4D2B] text-white flex items-center justify-center">
                <UserIcon size={26} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {user?.is_demo && (
                  <span data-testid="demo-mode-badge" className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F59E0B] text-white">
                    Demo Admin
                  </span>
                )}
                {user?.is_premium && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1E4D2B] text-white flex items-center gap-1">
                    <Crown size={10} /> Premium
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 leading-tight truncate mt-1">
                {d.welcome}, {user?.full_name || user?.email?.split("@")[0]}!
              </h1>
              <p className="text-stone-600 text-sm sm:text-base mt-0.5">{d.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 2x2 Dynamic Stats Grid from Agmarknet /prices/stats */}
      <section data-testid="dashboard-stats-grid" className="container-gc px-4 sm:px-6 pt-6 sm:pt-8">
        {statsLoading || !stats ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="card-gc p-4 sm:p-5 h-32 animate-pulse-subtle bg-stone-100" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* CARD 1 · Modal Price */}
            <article data-testid="stat-modal-price" className="card-gc p-4 sm:p-5 flex flex-col justify-between min-h-[128px]">
              <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#1E4D2B] leading-tight">
                  {d.stats_modal_title}
                </div>
                <div className="w-8 h-8 rounded-lg bg-[#F4F1EA] text-[#1E4D2B] flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={14} />
                </div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-stone-900 leading-tight">
                  ₹{stats.modal_price}
                  <span className="text-sm font-medium text-stone-500 ml-1">/kg</span>
                </div>
                <div className="mt-1 text-[11px] text-stone-500 leading-tight">{d.stats_modal_sub}</div>
              </div>
              <div
                data-testid="stat-modal-change-badge"
                className={`self-start mt-2 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                  stats.price_change_yesterday > 0
                    ? "bg-green-100 text-green-800"
                    : stats.price_change_yesterday < 0
                    ? "bg-red-100 text-red-700"
                    : "bg-stone-100 text-stone-600"
                }`}
              >
                {stats.price_change_yesterday > 0 ? <TrendingUp size={11} /> : stats.price_change_yesterday < 0 ? <TrendingDown size={11} /> : <Minus size={11} />}
                {stats.price_change_yesterday > 0 ? "+" : ""}{stats.price_change_yesterday}%
              </div>
            </article>

            {/* CARD 2 · Best Rate */}
            <article data-testid="stat-best-rate" className="card-gc p-4 sm:p-5 flex flex-col justify-between min-h-[128px]">
              <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#D97706] leading-tight">
                  {d.stats_best_title}
                </div>
                <div className="w-8 h-8 rounded-lg bg-[#FFF8EB] text-[#D97706] flex items-center justify-center flex-shrink-0">
                  <Award size={14} />
                </div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-stone-900 leading-tight">
                  ₹{stats.max_price}
                  <span className="text-sm font-medium text-stone-500 ml-1">/kg</span>
                </div>
                <div className="mt-1 text-[11px] text-stone-500 leading-tight truncate">
                  {d.stats_best_sub_prefix} <strong className="text-stone-700">{stats.max_price_mandi_name}</strong>
                  {stats.max_price_state ? `, ${stats.max_price_state}` : ""}
                </div>
              </div>
              <div className="self-start mt-2 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#F59E0B] text-white">
                <Sparkles size={11} /> {d.stats_best_badge}
              </div>
            </article>

            {/* CARD 3 · Total Arrivals */}
            <article data-testid="stat-total-arrivals" className="card-gc p-4 sm:p-5 flex flex-col justify-between min-h-[128px]">
              <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#14361E] leading-tight">
                  {d.stats_arrival_title}
                </div>
                <div className="w-8 h-8 rounded-lg bg-[#F4F1EA] text-[#14361E] flex items-center justify-center flex-shrink-0">
                  <Package size={14} />
                </div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-stone-900 leading-tight">
                  {stats.total_arrival_qty.toLocaleString("en-IN")}
                  <span className="text-sm font-medium text-stone-500 ml-1">{d.stats_arrival_unit}</span>
                </div>
                <div className="mt-1 text-[11px] text-stone-500 leading-tight">{d.stats_arrival_sub}</div>
              </div>
              <div
                data-testid="stat-arrival-trend-badge"
                className={`self-start mt-2 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                  stats.arrival_trend_vs_avg > 0
                    ? "bg-green-100 text-green-800"
                    : stats.arrival_trend_vs_avg < 0
                    ? "bg-red-100 text-red-700"
                    : "bg-stone-100 text-stone-600"
                }`}
              >
                {stats.arrival_trend_vs_avg > 0 ? <TrendingUp size={11} /> : stats.arrival_trend_vs_avg < 0 ? <TrendingDown size={11} /> : <Minus size={11} />}
                {stats.arrival_trend_vs_avg > 0 ? "+" : ""}{stats.arrival_trend_vs_avg}% {d.stats_arrival_vs_avg}
              </div>
            </article>

            {/* CARD 4 · Active Mandis */}
            <article data-testid="stat-active-mandis" className="card-gc p-4 sm:p-5 flex flex-col justify-between min-h-[128px]">
              <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#1E4D2B] leading-tight">
                  {d.stats_active_title}
                </div>
                <div className="w-8 h-8 rounded-lg bg-[#F4F1EA] text-[#1E4D2B] flex items-center justify-center flex-shrink-0">
                  <Activity size={14} />
                </div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-stone-900 leading-tight">
                  {stats.active_mandi_count}
                  <span className="text-sm font-medium text-stone-500 ml-1">{d.stats_active_of} {stats.total_tracked}</span>
                </div>
                <div className="mt-1 text-[11px] text-stone-500 leading-tight">{d.stats_active_sub}</div>
              </div>
              <div className="self-start mt-2 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                <BadgeCheck size={11} /> {stats.mandi_percentage_active}% {d.stats_active_active}
              </div>
            </article>
          </div>
        )}
      </section>

      {/* Main grid */}
      <section className="container-gc px-4 sm:px-6 py-8 sm:py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* User Profile Card */}
          <article
            data-testid="dash-profile-card"
            className="card-gc p-6 flex flex-col md:col-span-2 lg:col-span-1"
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">
              <BadgeCheck size={14} className="text-[#1E4D2B]" /> {d.profile_title}
            </div>
            <div className="space-y-2.5 flex-1">
              <div className="flex items-center gap-2 text-stone-900 font-semibold">
                <UserIcon size={16} className="text-stone-400" />
                <span className="truncate">{user?.full_name || "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-stone-700 text-sm">
                <Mail size={16} className="text-stone-400 flex-shrink-0" />
                <span className="truncate">{user?.email}</span>
              </div>
              <div className="flex items-center gap-2 text-stone-700 text-sm">
                <Phone size={16} className="text-stone-400 flex-shrink-0" />
                <span className="truncate">{user?.phone || d.profile_add_phone}</span>
              </div>
              <div className="flex items-center gap-2 text-stone-700 text-sm">
                <MapPin size={16} className="text-stone-400 flex-shrink-0" />
                <span className="truncate">{user?.city || "—"}</span>
              </div>
              <span className="inline-block text-xs px-2.5 py-1 rounded-full bg-[#F4F1EA] text-[#1E4D2B] font-semibold capitalize mt-1">
                {user?.user_role}
              </span>
            </div>
            <button
              data-testid="dash-edit-profile-btn"
              onClick={() => navigate("/profile")}
              className="btn-ghost mt-5 justify-center"
            >
              <Edit3 size={15} /> {d.profile_edit}
            </button>
          </article>

          {/* Grid items (Khaad, Beej, Government Schemes) — now clickable */}
          {grid.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.testid}
                to={it.to}
                data-testid={it.testid}
                className="card-gc p-6 flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all group"
              >
                <div className={`w-12 h-12 rounded-xl ${it.color} text-white flex items-center justify-center mb-4`}>
                  <Icon size={22} />
                </div>
                <div className="flex items-baseline gap-2 mb-1.5">
                  <h3 className="text-xl font-bold text-stone-900">{it.title}</h3>
                  <span className="text-xs font-medium text-stone-500">{it.sub}</span>
                </div>
                <p className="text-stone-600 leading-relaxed text-sm flex-1">{it.desc}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E4D2B] group-hover:gap-2 transition-all self-start">
                  {d.explore} <ArrowRight size={14} />
                </div>
              </Link>
            );
          })}

          {/* Subscription Plan Card — COMING SOON mode */}
          <article
            data-testid="dash-subscription-card"
            className="p-6 sm:p-7 md:col-span-2 lg:col-span-3 relative overflow-hidden rounded-2xl text-white shadow-sm"
            style={{ background: "linear-gradient(135deg, #1E4D2B 0%, #14361E 100%)" }}
          >
            <div className="absolute -right-16 -top-20 w-64 h-64 rounded-full bg-white/5" />
            <div className="absolute -left-10 -bottom-24 w-64 h-64 rounded-full bg-white/5" />

            {/* Coming Soon banner */}
            <div
              data-testid="subscription-coming-soon-banner"
              className="relative mb-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B] text-white text-xs sm:text-sm font-semibold shadow"
            >
              <Sparkles size={14} /> {d.sub_coming_soon_badge}
            </div>

            <div className="relative grid md:grid-cols-2 gap-6 items-center">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#F59E0B] mb-2">
                  <Crown size={14} /> {d.sub_title}
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold leading-tight">
                  {d.coming_soon}
                </h2>
                <p className="text-white/80 mt-2 text-sm sm:text-base leading-relaxed">
                  Enjoy full access — completely free for now. Paid plans (<strong className="text-white">{d.sub_price}</strong>) will roll out later.
                </p>
              </div>
              <div className="space-y-2.5" aria-disabled="true">
                {[
                  { icon: Smartphone, label: d.pay_upi, testid: "pay-upi-btn" },
                  { icon: CreditCard, label: d.pay_card, testid: "pay-card-btn" },
                  { icon: Building2, label: d.pay_netbanking, testid: "pay-netbanking-btn" },
                ].map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <button
                      key={i}
                      data-testid={p.testid}
                      disabled
                      className="w-full h-11 rounded-xl bg-white/5 border border-white/10 text-white/50 text-sm font-medium flex items-center justify-between px-4 cursor-not-allowed"
                    >
                      <span className="flex items-center gap-2.5">
                        <Icon size={16} />
                        {p.label}
                      </span>
                      <span className="text-xs">{d.coming_soon}</span>
                    </button>
                  );
                })}
                <p className="text-xs text-white/60 pt-1 flex items-center gap-1.5">
                  <ShieldCheck size={13} /> {d.pay_note}
                </p>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Log Out block */}
      <section className="container-gc px-4 sm:px-6 pb-16">
        <div
          data-testid="logout-block"
          className="card-gc p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-red-200/70"
        >
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
              <LogOut size={18} />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-stone-900">{d.logout_title}</h3>
              <p className="text-sm text-stone-600 mt-0.5">{d.logout_desc}</p>
            </div>
          </div>
          <button
            data-testid="dashboard-logout-btn"
            onClick={handleLogout}
            className="h-11 px-5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm inline-flex items-center gap-2 transition-colors active:scale-[0.98] flex-shrink-0"
          >
            <LogOut size={16} /> {d.logout_btn} <ArrowRight size={14} />
          </button>
        </div>
        <PriceDisclaimer className="mt-6" />
      </section>
    </div>
  );
};

export default PostLoginDashboard;
